import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/LanguageContext";

const STORAGE_KEY = "pt_consent";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const ConsentBanner = () => {
  const { t } = useLang();
  const c = t.consent;
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) setVisible(true);
    else {
      setAnalytics(!!stored.analytics);
      setMarketing(!!stored.marketing);
    }
  }, []);

  const openSettings = useCallback(() => {
    const stored = readConsent();
    setAnalytics(!!stored?.analytics);
    setMarketing(!!stored?.marketing);
    setManage(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, [openSettings]);

  const persist = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...choice, essential: true, ts: new Date().toISOString(), v: 1 })
    );
    setVisible(false);
    setManage(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="consent-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-[70] p-4 sm:p-6"
        >
          <div className="max-w-3xl mx-auto rounded-2xl bg-[#121118] border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-6 sm:p-7">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <Cookie size={18} className="text-[var(--neon-cyan)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-future text-base text-white">{c.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {c.body}{" "}
                  <Link to="/privacy" className="underline text-white/80 hover:text-white" data-testid="consent-privacy-link">
                    {c.privacy}
                  </Link>
                </p>
              </div>
            </div>

            {manage && (
              <div className="mt-6 space-y-4" data-testid="consent-manage">
                {[
                  { key: "essential", state: true, disabled: true, on: () => {} },
                  { key: "analytics", state: analytics, disabled: false, on: setAnalytics },
                  { key: "marketing", state: marketing, disabled: false, on: setMarketing },
                ].map((row) => (
                  <div key={row.key} className="flex items-start justify-between gap-4 rounded-xl bg-white/[0.03] border border-white/10 p-4">
                    <div>
                      <p className="text-sm text-white font-medium">{c.categories[row.key].name}</p>
                      <p className="text-xs text-white/50 mt-1 leading-relaxed">{c.categories[row.key].desc}</p>
                    </div>
                    {row.disabled ? (
                      <span className="label-caps text-[var(--neon-cyan)] whitespace-nowrap pt-1">{c.always}</span>
                    ) : (
                      <Switch
                        checked={row.state}
                        onCheckedChange={row.on}
                        data-testid={`consent-toggle-${row.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
              {manage ? (
                <button
                  data-testid="consent-save"
                  onClick={() => persist({ analytics, marketing })}
                  className="label-caps px-6 py-3 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300"
                >
                  {c.save}
                </button>
              ) : (
                <>
                  <button
                    data-testid="consent-manage-btn"
                    onClick={() => setManage(true)}
                    className="label-caps px-6 py-3 rounded-full border border-white/20 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
                  >
                    {c.manage}
                  </button>
                  <button
                    data-testid="consent-reject"
                    onClick={() => persist({ analytics: false, marketing: false })}
                    className="label-caps px-6 py-3 rounded-full border border-white/20 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
                  >
                    {c.reject}
                  </button>
                  <button
                    data-testid="consent-accept"
                    onClick={() => persist({ analytics: true, marketing: true })}
                    className="label-caps px-6 py-3 rounded-full bg-white text-black hover:bg-[var(--neon-cyan)] transition-colors duration-300"
                  >
                    {c.acceptAll}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const openCookieSettings = () => window.dispatchEvent(new Event("open-cookie-settings"));
